import React from 'react';
import TextField from '@mui/material/TextField';
import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { VariableSizeList, ListChildComponentProps } from 'react-window';
import { useResetCache } from 'shared/hooks/useResetCache';

const LISTBOX_PADDING = 8; // px

function renderRow(props: ListChildComponentProps) {
  const {
    data: { itemData, rowVisibleFieldName },
    index,
    style,
  } = props;
  const dataset = itemData[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + LISTBOX_PADDING,
  };

  if (Object.hasOwnProperty.call(dataset, 'group')) {
    return (
      <ListSubheader
        key={dataset.key}
        component="div"
        style={inlineStyle}
      >
        {dataset.group}
      </ListSubheader>
    );
  }

  return (
    <Typography
      component="li"
      {...dataset[0]}
      noWrap
      style={inlineStyle}
    >
      {dataset[1][rowVisibleFieldName]}
    </Typography>
  );
}

const OuterElementContext = React.createContext({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const outerProps = React.useContext(OuterElementContext);
  return (
    <div
      ref={ref}
      {...props}
      {...outerProps}
    />
  );
});

// Adapter for react-window
const ListboxComponent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & { rowVisibleFieldName: string }
>((props, ref) => {
  const { children, rowVisibleFieldName, ...other } = props;
  const itemData: React.ReactElement[] = [];
  (children as React.ReactElement[]).forEach((item: React.ReactElement & { children?: React.ReactElement[] }) => {
    itemData.push(item);
    itemData.push(...(item.children || []));
  });

  const itemCount = itemData.length;
  const itemSize = 36;

  const getChildSize = (child: React.ReactElement) => {
    if (Object.hasOwnProperty.call(child, 'group')) {
      return itemSize;
    }

    return itemSize;
  };

  const getHeight = () => {
    if (itemCount > 8) {
      return 8 * itemSize;
    }
    return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
  };

  const gridRef = useResetCache(itemCount);

  const dataset = {
    itemData,
    rowVisibleFieldName,
  };

  return (
    <div ref={ref}>
      <OuterElementContext.Provider value={other}>
        <VariableSizeList
          itemData={dataset}
          height={getHeight() + 2 * LISTBOX_PADDING}
          width="100%"
          ref={gridRef}
          outerElementType={OuterElementType}
          innerElementType="ul"
          itemSize={(index) => getChildSize(dataset.itemData[index])}
          overscanCount={5}
          itemCount={itemCount}
        >
          {renderRow}
        </VariableSizeList>
      </OuterElementContext.Provider>
    </div>
  );
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0,
    },
  },
});

type VirtualListPropsType<FieldType> = {
  onChange: (...event: any[]) => void;
  list: Array<FieldType & { id: number; title: string | null }>;
  label: string;
  getOptionLabel: (option: FieldType) => string;
  rowVisibleFieldName: keyof FieldType;
  required?: boolean;
};

export const VirtualSelect = <FieldType,>({
  onChange,
  list,
  label,
  getOptionLabel,
  rowVisibleFieldName,
  required = true,
}: VirtualListPropsType<FieldType>) => (
  <Autocomplete
    onChange={(_, value) => onChange(value)}
    disableListWrap
    PopperComponent={StyledPopper}
    ListboxComponent={(props) => (
      <ListboxComponent
        rowVisibleFieldName={rowVisibleFieldName as string}
        {...props}
      />
    )}
    options={list}
    multiple
    noOptionsText="Не найдено"
    getOptionLabel={getOptionLabel}
    renderInput={(params) => (
      <TextField
        {...params}
        multiline
        label={label}
        required={required}
        placeholder=""
        fullWidth
        size="small"
        SelectProps={{ multiple: true }}
        InputLabelProps={{
          shrink: true,
        }}
        sx={{
          marginBottom: '24px',
        }}
      />
    )}
    renderOption={(props, option, state) => [props, option, state.index] as React.ReactNode}
  />
);
